/**
 * Living inside the GeoStack portal (geostack.com/gis/), the way Command Creator and ERImager do.
 *
 * - Sign-in belongs to the portal. It launches every app with the Supabase session on the query
 *   string (`access_token`, `refresh_token`, and `token` as a copy); takePortalTokens() lifts them
 *   off. The Supabase client also uses the default storage key, the one the portal uses, so on
 *   geostack.com the two apps share a single session: sign in once, sign out once.
 * - The API is same-origin. geostack-router forwards /gis/api to the Fly backend, which answers
 *   under that prefix too, so there is no CORS and the fly.dev hostname stays out of the page.
 *
 * Anywhere else (localhost, ultimate-gis.pages.dev) the app keeps its own sign-in page and calls
 * REACT_APP_BACKEND_URL directly.
 */

export const PORTAL_URL = 'https://geostack.com/portal/';

const TOKEN_PARAMS = ['access_token', 'refresh_token', 'token'];

export const onGeostack = (loc = window.location) => loc.hostname === 'geostack.com';

/** Base the API paths (/api/...) are appended to. */
export function apiBaseUrl(loc = window.location) {
  const base = onGeostack(loc)
    ? `${loc.origin}${process.env.PUBLIC_URL || ''}`
    : process.env.REACT_APP_BACKEND_URL || '';
  return base.replace(/\/$/, '');
}

/** The portal's sign-in, set to bring the person back to `returnTo` afterwards. */
export function portalSignInUrl(returnTo = window.location.href) {
  const url = new URL(PORTAL_URL);
  url.searchParams.set('next', returnTo);
  return url.toString();
}

/**
 * The session the portal launched us with, removed from the address bar (tokens must not stay
 * in the history entry or leak in a Referer). Null when the URL carries none.
 */
export function takePortalTokens(win = window) {
  const url = new URL(win.location.href);
  const accessToken = url.searchParams.get('access_token') || url.searchParams.get('token');
  const refreshToken = url.searchParams.get('refresh_token');
  if (!accessToken && !refreshToken) return null;

  TOKEN_PARAMS.forEach((p) => url.searchParams.delete(p));
  win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash);

  return accessToken && refreshToken ? { accessToken, refreshToken } : null;
}
